import { ErrorDisplay } from "../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { useDateTime } from "../../../../modules/localization/useDateTime"

import { selectMeetingServiceConfig } from "../components/meetingService/MeetingServiceFormContainer"
import { isClosingDate } from "../components/shop/delivery/helpers"
import { ConfigQuery, fetchMeetingServiceConfiguration } from "../createOrderApi"
import { CreateOrderDialogMode } from "../createOrderTypes"
import { canOverrideClosingDates } from "../helpers"
import { useCreateOrderForm } from "../mainForm/CreateOrderFormContext"

import { useCreateOrderDialogType } from "./useCreateOrderDialogType"

// "admin": may order on a closing date, so the notice explains the privilege.
// "editing": the order already had the date, so the notice explains it may stay.
export type ClosedDeliveryDateAudience = "admin" | "editing"

function pickAudience(
    isRegularOrder: boolean,
    isEditing: boolean,
    canOverride: boolean,
): ClosedDeliveryDateAudience | null {
    // Refund and supplementary dialogs cannot change the delivery date at all.
    if (!isRegularOrder) return null
    // Editing wins over the privilege, merchants included: nobody placing an order here, so
    // "you may create it" would describe an action that is not happening.
    if (isEditing) return "editing"

    return canOverride ? "admin" : null
}

/**
 * Which closed-delivery-date notice belongs on screen, if any.
 *
 * Two ways to get there: whoever may order on a closing date picked one, or an order is being
 * edited that already had it — a regular user cannot pick one, so in EDIT it came with the order.
 *
 * Only for regular orders: refund and supplementary dialogs cannot change the delivery date at
 * all, so the notice there is noise nobody can act on.
 */
export function useClosedDeliveryDateNotice(
    merchantId: number | undefined,
    deliveryDate: string | null,
): ClosedDeliveryDateAudience | null {
    const { user, orderContext } = useCreateOrderForm()
    const { isRegularOrder } = useCreateOrderDialogType()
    const dateTimeFactory = useDateTime()

    const audience = pickAudience(
        isRegularOrder,
        orderContext.mode === CreateOrderDialogMode.EDIT,
        canOverrideClosingDates(user.userType),
    )

    const { data: meetingServiceConfig } = useApiQuery(fetchMeetingServiceConfiguration, {
        queryName: ConfigQuery.MEETING_SERVICE_CONFIG,
        dependencies: { userId: user.userId, merchantId: merchantId ?? 0 },
        select: selectMeetingServiceConfig,
        enabled: !!audience && !!merchantId && !!deliveryDate,
        // This subscriber only reads what the form already fetched. It enables later than the
        // form does — once a delivery date exists — and the default 10s staleness would make
        // that late enable refetch the config; never going stale keeps it a pure cache read.
        staleTime: Infinity,
        // The form owns this query's failure through its error boundary; a second subscriber
        // reporting it would put a snackbar on top of that.
        errorDisplay: ErrorDisplay.NONE,
    })

    if (!audience || !deliveryDate || !meetingServiceConfig) return null

    const isClosed = isClosingDate(
        dateTimeFactory.fromISO(deliveryDate),
        meetingServiceConfig.closingDates ?? [],
        dateTimeFactory,
    )

    return isClosed ? audience : null
}
